import _ from 'lodash';
import moment from 'moment';
import { ATTACHMENT_TYPES, TOAST_TYPES } from '../../constants/settings.constants';
import { attachmentBody } from '../../lib/dossier.utils';
import { addStakeholderBody, updateStakeholderBody } from '../../lib/stakeholder.utils';
import { attachments, procedure } from '../bff.api';
import responseValidators from '../response.validators';
import toastService from '../toasts.service';

const _updateBody = (data) => {
  const result = _.pick(data, ['startDate', 'endDate']);

  if (result.startDate) {
    result.startDate = moment(result.startDate, 'DD/MM/YYYY').format('YYYY-MM-DD');
  }

  if (result.endDate) {
    result.endDate = moment(result.endDate, 'DD/MM/YYYY').format('YYYY-MM-DD');
  }

  return result;
};

const _goalUpdateBody = (data) => {
  return _.pick(data, ['name', 'evaluation']);
};

const _partUpdateBody = (data) => {
  const dates = _updateBody(data);
  return { ..._.pick(data, ['name', 'note']), ...dates };
};

const _indicatorUpdateBody = (data) => {
  return _.pick(data, ['name']);
};

const _measureMomentUpdateBody = (data) => {
  return _.pick(data, ['date', 'method', 'determination']);
};

const update = async (dossierId, data, options = {}) => {
  return procedure
    .update(dossierId, _updateBody(data))
    .then((resp) => {
      responseValidators.updateProcedure(resp);
      if (options.onSuccess) {
        options.onSuccess(resp);
      }
    })
    .catch((err) => {
      if (options.onError) {
        options.onError(err.message);
      }
    });
};

const addPart = async (dossierId, options = {}) => {
  return procedure
    .addPart(dossierId)
    .then((resp) => {
      responseValidators.addProcedurePart(resp);
      if (options.onSuccess) {
        options.onSuccess(resp.json);
      }
    })
    .catch((err) => {
      if (options.onError) {
        options.onError(err.message);
      }
    });
};

const updatePart = async (dossierId, partId, data, options = {}) => {
  return procedure
    .updatePart(dossierId, partId, _partUpdateBody(data))
    .then((resp) => {
      responseValidators.updateProcedurePart(resp);
      if (options.onSuccess) {
        options.onSuccess(resp.json);
      }
    })
    .catch((err) => {
      if (options.onError) {
        options.onError(err.message);
      }
    });
};

const deletePart = async (dossierId, partId, options = {}) => {
  return procedure
    .deletePart(dossierId, partId)
    .then((resp) => {
      responseValidators.deleteProcedurePart(resp);
      if (options.onSuccess) {
        options.onSuccess(resp.json);
      }
    })
    .catch((err) => {
      if (options.onError) {
        options.onError(err.message);
      }
    });
};

const addGoal = async (dossierId, options = {}) => {
  return procedure
    .addGoal(dossierId)
    .then((resp) => {
      responseValidators.addProcedureGoal(resp);
      if (options.onSuccess) {
        options.onSuccess(resp.json);
      }
    })
    .catch((err) => {
      if (options.onError) {
        options.onError(err.message);
      }
    });
};

const updateGoal = async (dossierId, goalId, data, options = {}) => {
  return procedure
    .updateGoal(dossierId, goalId, _goalUpdateBody(data))
    .then((resp) => {
      responseValidators.updateProcedureGoal(resp);
      if (options.onSuccess) {
        options.onSuccess(resp.json);
      }
    })
    .catch((err) => {
      if (options.onError) {
        options.onError(err.message);
      }
    });
};

const deleteGoal = async (dossierId, goalId, options = {}) => {
  return procedure
    .deleteGoal(dossierId, goalId)
    .then((resp) => {
      responseValidators.deleteProcedureGoal(resp);
      if (options.onSuccess) {
        options.onSuccess(resp.json);
      }
    })
    .catch((err) => {
      if (options.onError) {
        options.onError(err.message);
      }
    });
};

const addGoalIndicator = async (dossierId, goalId, options = {}) => {
  return procedure
    .addGoalIndicator(dossierId, goalId)
    .then((resp) => {
      responseValidators.addProcedureGoalIndicator(resp);
      if (options.onSuccess) {
        options.onSuccess(resp.json);
      }
    })
    .catch((err) => {
      if (options.onError) {
        options.onError(err.message);
      }
    });
};

const updateGoalIndicator = async (dossierId, goalId, indicatorId, data, options = {}) => {
  return procedure
    .updateGoalIndicator(dossierId, goalId, indicatorId, _indicatorUpdateBody(data))
    .then((resp) => {
      responseValidators.updateProcedureGoalIndicator(resp);
      if (options.onSuccess) {
        options.onSuccess(resp.json);
      }
    })
    .catch((err) => {
      if (options.onError) {
        options.onError(err.message);
      }
    });
};

const deleteGoalIndicator = async (dossierId, goalId, indicatorId, options = {}) => {
  return procedure
    .deleteGoalIndicator(dossierId, goalId, indicatorId)
    .then((resp) => {
      responseValidators.deleteProcedureGoalIndicator(resp);
      if (options.onSuccess) {
        options.onSuccess(resp.json);
      }
    })
    .catch((err) => {
      if (options.onError) {
        options.onError(err.message);
      }
    });
};

const addIndicatorMeasureMoment = async (dossierId, goalId, indicatorId, data, options = {}) => {
  return procedure
    .addIndicatorMeasureMoment(dossierId, goalId, indicatorId, _measureMomentUpdateBody(data))
    .then((resp) => {
      responseValidators.addIndicatorMeasureMoment(resp);
      if (data.attachments && data.attachments.length > 0) {
        return Promise.all(
          data.attachments.map((att) =>
            attachments.uploadForDossier(
              dossierId,
              attachmentBody(resp?.json?.id, ATTACHMENT_TYPES.MEASURE_MOMENT, att)
            )
          )
        ).then((resps) => {
          if (!resps.every((r) => r.status === 200)) {
            toastService.showToast(TOAST_TYPES.WARNING, 'Kon een of meer bijlagen voor je meetmoment niet uploaden.');
          }
          if (options.onSuccess) {
            options.onSuccess(resp.json);
          }
        });
      }
      if (options.onSuccess) {
        options.onSuccess(resp.json);
      }
    })
    .catch((err) => {
      if (options.onError) {
        options.onError(err.message);
      }
    });
};

const updateIndicatorMeasureMoment = async (dossierId, goalId, indicatorId, measureMomentId, data, options = {}) => {
  return procedure
    .updateIndicatorMeasureMoment(dossierId, goalId, indicatorId, measureMomentId, _measureMomentUpdateBody(data))
    .then((resp) => {
      responseValidators.updateIndicatorMeasureMoment(resp);
      if (options.onSuccess) {
        options.onSuccess(resp.json);
      }
    })
    .catch((err) => {
      if (options.onError) {
        options.onError(err.message);
      }
    });
};

const deleteIndicatorMeasureMoment = async (dossierId, goalId, indicatorId, measureMomentId, options = {}) => {
  return procedure
    .deleteIndicatorMeasureMoment(dossierId, goalId, indicatorId, measureMomentId)
    .then((resp) => {
      responseValidators.deleteIndicatorMeasureMoment(resp);
      if (options.onSuccess) {
        options.onSuccess(resp.json);
      }
    })
    .catch((err) => {
      if (options.onError) {
        options.onError(err.message);
      }
    });
};

const addStakeholder = async (dossierId, _itemId, data, options = {}) => {
  return procedure
    .addStakeholder(dossierId, addStakeholderBody(data))
    .then((resp) => {
      responseValidators.addProcedureStakeholder(resp);
      if (options.onSuccess) {
        options.onSuccess(resp.json);
      }
    })
    .catch((err) => {
      if (options.onError) {
        options.onError(err.message);
      }
    });
};

const updateStakeholder = async (dossierId, _itemId, stakeholderId, data, options = {}) => {
  return procedure
    .updateStakeholder(dossierId, stakeholderId, updateStakeholderBody(data))
    .then((resp) => {
      responseValidators.updateProcedureStakeholder(resp);
      if (options.onSuccess) {
        options.onSuccess(resp.json);
      }
    })
    .catch((err) => {
      if (options.onError) {
        options.onError(err.message);
      }
    });
};

const removeStakeholder = async (dossierId, _itemId, stakeholderId, options = {}) => {
  return procedure
    .removeStakeholder(dossierId, stakeholderId)
    .then((resp) => {
      responseValidators.removeProcedureStakeholder(resp);
      if (options.onSuccess) {
        options.onSuccess(resp.json);
      }
    })
    .catch((err) => {
      if (options.onError) {
        options.onError(err.message);
      }
    });
};

const procedureService = {
  update,
  addStakeholder,
  updateStakeholder,
  removeStakeholder,
  addPart,
  updatePart,
  deletePart,
  addGoal,
  updateGoal,
  deleteGoal,
  addGoalIndicator,
  updateGoalIndicator,
  deleteGoalIndicator,
  addIndicatorMeasureMoment,
  updateIndicatorMeasureMoment,
  deleteIndicatorMeasureMoment
};

export default procedureService;
